import { AppContext } from '@ctsu/loader';
import Buffer from 'buffer';
import { lensProp, prop, sortBy, uniq, view } from 'ramda';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const HIST_ST_QRY_TA = 'taq'; // HistoryState query key used by SiteDtl\ViewManage\TaskAssignment component
export const HIST_ST_QRY_TAB = 'tabq'; // HistoryState query key used by TAB\Browser component
export const HIST_ST_QRY_TB = 'tbq'; // HistoryState query key used by Template\Browser component
export const HIST_ST_QRY_DAP = 'apq'; // HistoryState query key used by Bulk Approval component
export const HIST_ST_QRY_RB = 'rbq'; //HistoryState query key used by Request Browser
export const HIST_ST_QRY_SB = 'sbq'; //HistoryState query key used by Site DTL Browser

// Deeplinking params
export const QRY_PARAM = 'p';
export const QRY_PARAM_PAGE = 'page';

export const useHistory = () => {
  const navigate = useNavigate();
  const history = {
    push: (pathname) => navigate({ pathname }),
    replace: (pathname) => navigate({ pathname, replace: true }),
    goBack: () => navigate(-1)
  };
  return history;
};

export function useCurrentUser() {
  const context = useContext(AppContext);
  const { session } = context;
  let userFirstLast = '';
  if (session) {
    userFirstLast = session.userFirstLast;
  }
  return userFirstLast;
}

export function useCurrentUserCtepId() {
  const context = useContext(AppContext);
  const { session } = context;
  let userCtepId = '';
  if (session) {
    userCtepId = session.ctepId;
  }
  return userCtepId;
}

// Check if current user is affiliated with a specific LPO/Roster
export function useIsAffiliated(roster) {
  const context = useContext(AppContext);
  const { session } = context;
  const { userGroups = [] } = session || [];
  let rosters = [];
  if (roster === 'PEP-CTN' || roster === 'COG') {
    rosters = ['PEP-CTN', 'COG'];
  } else {
    rosters = [roster];
  }

  if (userGroups && userGroups.length > 0) {
    return rosters.some((rost) => userGroups.indexOf(rost) > -1);
  }

  return false;
}
export function useGetFromAppConfig(a, b, sort = true) {
  const { application, session } = useContext(AppContext);
  let config = [];

  try {
    if (session) {
      const { appConfig } = application;
      const propOne = lensProp(a);
      const propTwo = lensProp(b);
      const child = JSON.parse(view(propOne, appConfig) || '[]');

      if (sort && Array.isArray(child) && child.length > 0) {
        config = sortBy(prop('label'))(view(propTwo, child));
      } else {
        config = view(propTwo, child);
      }
    }
  } catch (e) {
    console.log(e);
  }

  return config;
}
export function useUserAffiliations() {
  const context = useContext(AppContext);
  const { session } = context;
  const { userGroups = [] } = session || [];
  let group = userGroups;
  const cogGroup = ['PEP-CTN', 'COG'];
  const isCogGroup = cogGroup.some((rost) => userGroups.indexOf(rost) > -1);
  if (isCogGroup) {
    group = uniq([...userGroups, ...cogGroup]);
  }
  const leadGroupLov = useGetFromAppConfig('LOV_LPO', 'lpo');
  const leadGroup = leadGroupLov.map((lpo) => lpo.label);
  const lpo = group.filter((id) => leadGroup.indexOf(id) > -1);
  return lpo;
}

export function useRefresh() {
  const [value, setValue] = useState(0); // integer state
  const refresh = useCallback(() => {
    setValue(value + 1);
  }, []);
  return refresh;
}
export function useRefreshRoute() {
  const navigate = useNavigate();
  const memoizedGoBack = useCallback(() => {
    navigate('/temp');
  }, []);

  return memoizedGoBack;
}

export const useQuery = () => new URLSearchParams(useLocation().search);

export function useDecodedQueryParams(key) {
  const query = useQuery();
  const q = query.get(key) || '';
  let params = '';
  try {
    params = Buffer.from(q, 'base64').toString('utf-8');
  } catch (e) {
    console.log(e);
  }
  return new URLSearchParams(params) || {};
}

export function useHistoryState(key, defaultTo) {
  const navigate = useNavigate();
  const query = useQuery();

  const [state, rawSetState] = useState(() => {
    const q = query.get(key);
    const paramsFromSession = q && window.sessionStorage.getItem(key);
    return paramsFromSession || q || defaultTo;
  });

  function setState(value) {
    window.sessionStorage.setItem(key, value);
    navigate({ search: `${key}=${value}` });
    rawSetState(value);
  }

  return [state, setState];
}

export function useIsProduction() {
  const { application, session } = useContext(AppContext);
  let isProd = false;
  try {
    if (session) {
      isProd = application.isProduction;
    }
  } catch (e) {
    console.log(e);
  }

  return isProd;
}

export function usePrevLocation(location) {
  const prevLocRef = useRef(location);

  useEffect(() => {
    prevLocRef.current = location;
  }, [location]);

  return prevLocRef.current;
}
